import React, { useEffect, useState, useRef, useCallback } from 'react';
import { BrowserRouter, Routes, Route, useParams, useNavigate, useLocation, Link, useSearchParams } from 'react-router-dom';
import './App.css';
import AuthModal from './AuthModal';
import Onboarding from './Onboarding';
import { BookmarkListsPage, PublicListView, SaveToListDropdown } from './BookmarkLists';
import HomePage, { StoryPage } from './HomePage';
import AboutPage from './AboutPage';
import { I18nProvider, useI18n, localizeApiUrl } from './i18n/I18nContext';

interface Category {
  id: number;
  name: string;
  slug: string;
  subcategories: Category[];
}

interface Article {
  id: number;
  title: string;
  slug: string;
  summary: string;
  link: string;
  publisher: string;
  impact_factor: number;
  pub_date: string;
  authors: string | null;
  doi: string | null;
  article_type: string | null;
  vote_count: number;
  user_vote: number;
  jfif: number;
  is_open_access: boolean;
}

function timeAgo(dateStr: string): string {
  if (!dateStr) return 'recently';
  const date = new Date(dateStr);
  if (isNaN(date.getTime()) || date.getFullYear() <= 1970) return 'recently';
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days !== 1 ? 's' : ''} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months !== 1 ? 's' : ''} ago`;
  const years = Math.floor(months / 12);
  return `${years} year${years !== 1 ? 's' : ''} ago`;
}

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

function SidebarSearch() {
  const [q, setQ] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const oaChecked = searchParams.get('oa') === '1';
  const location = useLocation();
  const { t } = useI18n();

  const toggleOa = () => {
    const params = new URLSearchParams(searchParams);
    if (oaChecked) {
      params.delete('oa');
    } else {
      params.set('oa', '1');
    }
    const basePath = location.pathname.startsWith('/jf') ? location.pathname : '/jf';
    navigate(`${basePath}?${params.toString()}`);
  };

  return (
    <div style={{ padding: '8px' }}>
      <form onSubmit={(e) => { e.preventDefault(); if (q.trim()) navigate(`/jf?q=${encodeURIComponent(q.trim())}${oaChecked ? '&oa=1' : ''}`); }}>
        <input
          placeholder={t.sidebar_search_placeholder}
          value={q}
          onChange={e => setQ(e.target.value)}
          style={{ background: 'white', border: '1px solid #5f99cf', padding: '4px 6px', width: '100%', fontSize: 11, boxSizing: 'border-box' }}
        />
      </form>
      <label className="oa-filter-label" style={{ marginTop: 6 }}>
        <input type="checkbox" checked={oaChecked} onChange={toggleOa} />
        {' '}{t.sidebar_oa_only}
      </label>
    </div>
  );
}

function NavTabs() {
  const [searchParams] = useSearchParams();
  const currentSort = searchParams.get('sort') || 'hottest';
  const location = useLocation();
  const { t } = useI18n();
  const path = location.pathname;
  // Sort tabs apply to feed pages only — not trending, article, profile, etc.
  const isFeedPage = path.startsWith('/jf') && !path.startsWith('/jf/trending') && !path.startsWith('/jf/article') && !path.startsWith('/jf/profile') && !path.startsWith('/jf/submit') && !path.startsWith('/jf/lists') && !path.startsWith('/jf/hotp');
  const basePath = isFeedPage ? path : '/jf';

  return (
    <div className="nav-tabs">
      <Link to={`${basePath}?sort=hottest`} className={`nav-tab${isFeedPage && currentSort === 'hottest' ? ' active' : ''}`}>{t.nav_hottest}</Link>
      <Link to={`${basePath}?sort=newest`} className={`nav-tab${isFeedPage && currentSort === 'newest' ? ' active' : ''}`}>{t.nav_newest}</Link>
      <Link to="/jf?sort=all" className={`nav-tab${isFeedPage && path === '/jf' && currentSort === 'all' ? ' active' : ''}`}>{t.nav_all}</Link>
    </div>
  );
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      setTimeout(() => {
        const el = document.getElementById(hash.slice(1));
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);
  return null;
}

function Layout({ children, categories, user, setShowLogin, toggleTheme, theme, fetchMessages, token, t: tProp }: any) {
  const i18n = useI18n();
  const t = tProp || i18n.t;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const sidebarRef = useRef<HTMLElement>(null);
  const location = useLocation();
  const isHome = location.pathname === '/';

  const handleSidebarToggle = () => {
    const next = !sidebarOpen;
    setSidebarOpen(next);
    if (next) {
      setMobileMenuOpen(true);
      setTimeout(() => sidebarRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    }
  };

  return (
    <div className="App">
      <header>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Link to="/jf" className="logo">{t.site_name}</Link>
          <div className="nav-tabs header-nav-tabs">
            <Link to="/" className={`nav-tab${isHome ? ' active' : ''}`}>{t.nav_home}</Link>
            <Link to="/about" className={`nav-tab${location.pathname === '/about' ? ' active' : ''}`}>{t.nav_about}</Link>
            <Link to="/jf/trending" className={`nav-tab${location.pathname === '/jf/trending' ? ' active' : ''}`}>{t.nav_trending}</Link>
          </div>
          <NavTabs />
        </div>
        <div className="header-right">
          {user ? (
            <span className="header-auth-links">
              <Link to="/jf/profile" style={{ color: '#5f99cf', textDecoration: 'none', fontWeight: 'bold' }}>{user.username}</Link> |{' '}
              <Link to="/jf/lists" style={{ color: '#5f99cf', textDecoration: 'none' }}>{t.auth_my_lists}</Link> |{' '}
              <button onClick={fetchMessages} style={{ background: 'none', border: 'none', cursor: 'pointer', font: 'inherit', color: 'inherit', padding: 0 }}>{t.auth_messages}</button> |{' '}
              <button onClick={() => { localStorage.removeItem('token'); window.location.reload(); }} style={{ background: 'none', border: 'none', cursor: 'pointer', font: 'inherit', color: 'inherit', padding: 0 }}>{t.auth_logout}</button>
            </span>
          ) : (
            <span className="header-auth-links">{t.auth_join} <button onClick={() => setShowLogin(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', font: 'inherit', color: '#5f99cf', padding: 0, textDecoration: 'underline' }}>{t.auth_login_register}</button> {t.auth_in_seconds}</span>
          )}
          <span style={{ marginLeft: 10, cursor: 'pointer' }} onClick={toggleTheme}>{theme === 'light' ? '🌙' : '☀️'}</span>
          <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Menu">
            {mobileMenuOpen ? '✕' : '☰'}
          </button>
        </div>
      </header>

      {/* Mobile dropdown menu */}
      {mobileMenuOpen && (
        <div className="mobile-menu">
          <div className="mobile-menu-nav">
            <Link to="/" onClick={() => setMobileMenuOpen(false)} className={isHome ? 'active' : ''}>{t.nav_home}</Link>
            <Link to="/about" onClick={() => setMobileMenuOpen(false)} className={location.pathname === '/about' ? 'active' : ''}>{t.nav_about}</Link>
            <Link to="/jf/trending" onClick={() => setMobileMenuOpen(false)} className={location.pathname === '/jf/trending' ? 'active' : ''}>{t.nav_trending}</Link>
            <Link to="/jf?sort=hottest" onClick={() => setMobileMenuOpen(false)} className={location.pathname.startsWith('/jf') && !location.pathname.includes('/trending') && !location.pathname.includes('/article') && !location.pathname.includes('/profile') && !location.pathname.includes('/admin') && (new URLSearchParams(location.search).get('sort') || 'hottest') === 'hottest' ? 'active' : ''}>{t.nav_hottest}</Link>
            <Link to="/jf?sort=newest" onClick={() => setMobileMenuOpen(false)} className={location.pathname.startsWith('/jf') && (new URLSearchParams(location.search).get('sort')) === 'newest' ? 'active' : ''}>{t.nav_newest}</Link>
            <Link to="/jf?sort=all" onClick={() => setMobileMenuOpen(false)} className={location.pathname === '/jf' && (new URLSearchParams(location.search).get('sort')) === 'all' ? 'active' : ''}>{t.nav_all}</Link>
          </div>
          <div className="mobile-menu-auth">
            {user ? (
              <>
                <Link to="/jf/profile" onClick={() => setMobileMenuOpen(false)}>{user.username}</Link>
                <Link to="/jf/lists" onClick={() => setMobileMenuOpen(false)}>my lists</Link>
                <button onClick={() => { setMobileMenuOpen(false); fetchMessages(); }}>messages</button>
                <button onClick={() => { localStorage.removeItem('token'); window.location.reload(); }}>logout</button>
              </>
            ) : (
              <button onClick={() => { setMobileMenuOpen(false); setShowLogin(true); }}>login or register</button>
            )}
          </div>
          <div className="mobile-menu-auth">
            <button onClick={() => { setMobileMenuOpen(false); toggleTheme(); }}>
              {theme === 'light' ? '🌙 dark mode' : '☀️ light mode'}
            </button>
          </div>
        </div>
      )}

      <div className="container">
        <button className="sidebar-toggle" onClick={handleSidebarToggle}>
          {sidebarOpen ? t.sidebar_hide : t.sidebar_browse}
        </button>
        <main className="main-content">{children}</main>
        <aside ref={sidebarRef} className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
          <div className="sidebar-box" style={{ marginBottom: 10 }}>
            <SidebarSearch />
          </div>
          <div className="sidebar-box">
            <div className="sidebar-header">{t.sidebar_categories}</div>
            <div className="sidebar-content">
              {categories.map((cat: any) => (
                <CategoryItem key={cat.id} cat={cat} currentPath="" />
              ))}
            </div>
          </div>
        </aside>
      </div>
      <footer style={{ textAlign: 'center', padding: '20px', borderTop: '1px solid #5f99cf', marginTop: '20px', color: '#888', fontSize: '11px' }}>
        &copy; {new Date().getFullYear()} <a href="https://knowsyn.io" target="_blank" rel="noopener noreferrer" style={{ color: '#888', textDecoration: 'none' }}>Knowledge Syndicate</a>
        {' · '}
        {i18n.isChinese
          ? <a href="https://journalfeeds.com" style={{ color: '#888', textDecoration: 'none' }}>English</a>
          : <a href="https://cn.journalfeeds.com" style={{ color: '#888', textDecoration: 'none' }}>中文</a>
        }
      </footer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Article Detail Page
// ---------------------------------------------------------------------------

function ArticlePage(props: any) {
  const { token, user, setShowLogin, api: apiProp } = props;
  const i18n = useI18n();
  const api = apiProp || ((u: string) => localizeApiUrl(u, i18n.apiLang));
  const { slug } = useParams();
  const [article, setArticle] = useState<Article | null>(null);
  const [comments, setComments] = useState<any[]>([]);
  const [newComment, setNewComment] = useState('');
  const [replyTo, setReplyTo] = useState<number | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const commentsRef = useRef<HTMLDivElement>(null);
  const [aiSummary, setAiSummary] = useState<{available: boolean; plain_summary?: string; practice_summary?: string; source?: string} | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [practiceLoading, setPracticeLoading] = useState(false);
  const [showPractice, setShowPractice] = useState(false);
  const [fullText, setFullText] = useState<{available: boolean; content?: string; license?: string} | null>(null);
  const [fullTextLoading, setFullTextLoading] = useState(false);
  const [showFullText, setShowFullText] = useState(false);
  const [notes, setNotes] = useState<any[]>([]);
  const [noteText, setNoteText] = useState('');
  const [notePublic, setNotePublic] = useState(false);
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [citations, setCitations] = useState<{cited_by: any[]; references: any[]; cited_by_count: number; references_count: number} | null>(null);

  useEffect(() => {
    fetch(api(`/api/article/${slug}`))
      .then(res => res.json())
      .then(data => {
        setArticle(data);
        fetchComments(data.id);
        // Fetch notes
        const noteHeaders: Record<string, string> = {};
        if (token) noteHeaders['Authorization'] = `Bearer ${token}`;
        fetch(`/api/article/${data.id}/note`, { headers: noteHeaders })
          .then(r => r.json())
          .then(setNotes)
          .catch(() => {});
        // Fetch citation network
        fetch(`/api/article/${data.id}/citations`)
          .then(r => r.json())
          .then(setCitations)
          .catch(() => {});
      });
  }, [slug, token]);

  // Scroll to comments if URL has #comments
  useEffect(() => {
    if (article && window.location.hash === '#comments' && commentsRef.current) {
      commentsRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [article, comments]);

  const fetchComments = (articleId: number) => {
    fetch(`/api/article/${articleId}/comments`)
      .then(r => r.json())
      .then(setComments);
  };

  const postComment = async (parentId: number | null = null) => {
    if (!token) return setShowLogin(true);
    const content = parentId ? replyContent : newComment;
    if (!content.trim()) return;
    await fetch(`/api/article/${article!.id}/comments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ content, parent_id: parentId }),
    });
    if (parentId) {
      setReplyContent('');
      setReplyTo(null);
    } else {
      setNewComment('');
    }
    fetchComments(article!.id);
  };

  const renderComment = (c: any, depth = 0) => (
    <div key={c.id} style={{ marginLeft: depth * 20, marginBottom: 8, paddingLeft: depth > 0 ? 12 : 0, borderLeft: depth > 0 ? '1px solid #cee3f8' : 'none' }}>
      <div style={{ fontSize: 12 }}>
        <b style={{ color: '#5f99cf' }}>{c.username}</b>
        <span style={{ color: '#888', marginLeft: 8, fontSize: 10 }}>{c.created_at ? timeAgo(c.created_at) : ''}</span>
      </div>
      <div style={{ fontSize: 13, margin: '4px 0' }}>{c.content}</div>
      {token && (
        <button onClick={() => { setReplyTo(replyTo === c.id ? null : c.id); setReplyContent(''); }}
          className="link-btn" style={{ fontSize: 10 }}>
          {replyTo === c.id ? 'cancel' : 'reply'}
        </button>
      )}
      {replyTo === c.id && (
        <div style={{ marginTop: 6 }}>
          <textarea value={replyContent} onChange={e => setReplyContent(e.target.value)}
            placeholder="Write a reply..." style={{ width: '100%', height: 50, fontSize: 12, padding: 6, boxSizing: 'border-box', border: '1px solid #ccc' }} />
          <button onClick={() => postComment(c.id)} className="create-post-btn" style={{ fontSize: 11, padding: '3px 10px', marginTop: 4 }}>reply</button>
        </div>
      )}
      {c.replies && c.replies.map((r: any) => renderComment(r, depth + 1))}
    </div>
  );

  if (!article) return <Layout {...props}><div>Loading...</div></Layout>;

  return (
    <Layout {...props}>
      <div className="article-row" style={{ border: 'none' }}>
        <div className="article-body">
          <h1 className="article-title" style={{ fontSize: '22px' }}>
            {article.is_open_access && <span className="oa-badge" title="Open Access">OA</span>}
            <a href={article.link} target="_blank" rel="noopener noreferrer" dangerouslySetInnerHTML={{__html: article.title}} />
            <span className="domain">({article.publisher})</span>
          </h1>
          {article.authors && (
            <div className="article-authors">{article.authors}</div>
          )}
          <div className="meta-line">
            {article.article_type && <span className="article-type-badge">{article.article_type}</span>}
            submitted by <b>knowsyn</b> {timeAgo(article.pub_date)} |
            IF: {article.impact_factor || 'N/A'} | <b>{article.vote_count} points</b>
            {article.jfif > 0 && <> | {token
              ? <Link to={`/jf/article/${article.slug}/jfif`} className="jfif-badge jfif-link" title="View JFIF score breakdown">JFIF: {article.jfif.toFixed(0)}</Link>
              : <Link to="/about#jfif" className="jfif-badge jfif-link" title="JournalFeeds Impact Factor — log in to see calculation">JFIF: {article.jfif.toFixed(0)}</Link>
            }</>}
            {article.doi && (
              <> | DOI: <a href={`https://doi.org/${article.doi}`} target="_blank" rel="noopener noreferrer" style={{ color: '#5f99cf' }}>{article.doi}</a></>
            )}
          </div>

          <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="action-btn" onClick={() => {
              if (!token) { setShowLogin(true); return; }
              if (aiSummary) return;
              setSummaryLoading(true);
              fetch(`/api/article/${article.id}/summary`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).then(data => { setAiSummary(data); setSummaryLoading(false); });
            }}>
              {summaryLoading ? 'Generating...' : aiSummary?.available ? 'Summary loaded' : 'Explain this paper'}
            </button>
            {article.doi && article.is_open_access && (
              fullText?.available === false ? (
                <span style={{ display: 'inline-flex', gap: '6px', alignItems: 'center' }}>
                  <a className="action-btn" href={article.link} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                    Read on publisher site ↗
                  </a>
                  <button className="action-btn" style={{ fontSize: '11px' }} onClick={() => {
                    setFullTextLoading(true);
                    setFullText(null);
                    fetch(`/api/article/${article.id}/fulltext`, { headers: { Authorization: `Bearer ${token}` } })
                      .then(r => r.json()).then(data => {
                        setFullText(data);
                        if (data.available) setShowFullText(true);
                        setFullTextLoading(false);
                      });
                  }}>
                    {fullTextLoading ? 'Retrying...' : 'Retry fetch'}
                  </button>
                </span>
              ) : (
                <button className="action-btn" onClick={() => {
                  if (!token) { setShowLogin(true); return; }
                  if (fullText?.available) { setShowFullText(!showFullText); return; }
                  setFullTextLoading(true);
                  fetch(`/api/article/${article.id}/fulltext`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).then(data => { setFullText(data); if (data.available) setShowFullText(true); setFullTextLoading(false); });
                }}>
                  {fullTextLoading ? 'Fetching...' : showFullText ? 'Hide full text' : 'Read full text (OA)'}
                </button>
              )
            )}
          </div>

          {aiSummary?.available && (
            <div className="ai-summary-box">
              <div className="ai-summary-label">AI Summary <span style={{ fontWeight: 'normal', color: '#888' }}>(based on {aiSummary.source})</span></div>
              <p>{aiSummary.plain_summary}</p>
              {aiSummary.source !== 'title' && (
                <button className="action-btn" style={{ marginTop: 8, fontSize: 11 }} onClick={() => {
                  if (!token) { setShowLogin(true); return; }
                  if (aiSummary.practice_summary) { setShowPractice(!showPractice); return; }
                  setPracticeLoading(true);
                  fetch(`/api/article/${article!.id}/summary?practice=1`, { headers: { Authorization: `Bearer ${token}` } })
                    .then(r => r.json())
                    .then(data => {
                      if (data.practice_summary) {
                        setAiSummary({ ...aiSummary, practice_summary: data.practice_summary });
                        setShowPractice(true);
                      }
                      setPracticeLoading(false);
                    });
                }}>
                  {practiceLoading ? 'Generating...' : showPractice ? 'Hide practice takeaways' : 'So what does this mean?'}
                </button>
              )}
              {showPractice && aiSummary.practice_summary && (() => {
                try {
                  const ps = JSON.parse(aiSummary.practice_summary);
                  return (
                    <div className="practice-cards">
                      <div className="practice-card practice-card-clinician">
                        <div className="practice-card-header">For Clinicians</div>
                        <p>{ps.clinician}</p>
                      </div>
                      <div className="practice-card practice-card-industry">
                        <div className="practice-card-header">For Industry R&D</div>
                        <p>{ps.industry}</p>
                      </div>
                      <div className="practice-card practice-card-policy">
                        <div className="practice-card-header">For Policymakers</div>
                        <p>{ps.policy}</p>
                      </div>
                    </div>
                  );
                } catch { return null; }
              })()}
            </div>
          )}

          <div className="article-summary" style={{ marginTop: '20px', fontSize: '14px', lineHeight: '1.6' }} dangerouslySetInnerHTML={{__html: article.summary}} />

          {showFullText && fullText?.available && (
            <div className="full-text-box">
              <div className="full-text-label">Full Text <span style={{ fontWeight: 'normal', color: '#888' }}>({fullText.license})</span></div>
              <div dangerouslySetInnerHTML={{__html: fullText.content || ''}} />
              <div className="full-text-copyright">
                {article.authors && <>Authors: {article.authors}. </>}
                Published by {article.publisher}.
                {article.doi && <> DOI: <a href={`https://doi.org/${article.doi}`} target="_blank" rel="noopener noreferrer">{article.doi}</a>. </>}
                Licensed under {fullText.license || 'Open Access'}.
                <a href={article.link} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 4 }}>View original</a>.
              </div>
            </div>
          )}

          {/* Read Status + Notes */}
          <div style={{ marginTop: 24, padding: '12px 0', borderTop: '1px solid #eee' }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
              <ReadStatusButton articleId={article.id} token={token} user={user} setShowLogin={setShowLogin} />
              <button className="link-btn" onClick={() => { if (!token) { setShowLogin(true); return; } setShowNoteForm(!showNoteForm); }}>
                {showNoteForm ? 'cancel note' : '+ add note'}
              </button>
            </div>

            {showNoteForm && token && (
              <div style={{ marginBottom: 16, padding: 10, border: '1px solid #ddd', background: '#fafafa' }}>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 8 }}>
                  Take a note — notes connect to articles on lists and are curated only by you. Comment below to join a public discussion.
                </div>
                <textarea
                  value={noteText}
                  onChange={e => setNoteText(e.target.value)}
                  placeholder="Your note..."
                  maxLength={5000}
                  style={{ width: '100%', height: 80, fontSize: 13, padding: 8, boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: 4 }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
                  <label style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <input type="checkbox" checked={notePublic} onChange={e => setNotePublic(e.target.checked)} />
                    public note
                  </label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <span style={{ fontSize: 10, color: '#888' }}>{noteText.length}/5000</span>
                    <button
                      className="create-post-btn"
                      style={{ fontSize: 11, padding: '3px 12px' }}
                      onClick={async () => {
                        if (!noteText.trim()) return;
                        await fetch(`/api/article/${article.id}/note`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                          body: JSON.stringify({ note: noteText, public: notePublic }),
                        });
                        setNoteText('');
                        setShowNoteForm(false);
                        // Refresh notes
                        const headers: Record<string, string> = {};
                        if (token) headers['Authorization'] = `Bearer ${token}`;
                        fetch(`/api/article/${article.id}/note`, { headers }).then(r => r.json()).then(setNotes);
                      }}
                    >
                      save note
                    </button>
                  </div>
                </div>
              </div>
            )}

            {notes.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 6, color: '#555' }}>Notes</div>
                {notes.map((n: any) => (
                  <div key={n.id} style={{ marginBottom: 8, padding: '6px 10px', background: n.public ? '#f0f8f0' : '#f8f8f0', borderLeft: `3px solid ${n.public ? '#228B22' : '#DAA520'}`, fontSize: 13 }}>
                    <div style={{ marginBottom: 4 }}>{n.note}</div>
                    <div style={{ fontSize: 10, color: '#888' }}>
                      {n.username} | {n.public ? 'public' : 'private'} | {n.added_at ? timeAgo(n.added_at) : ''}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {citations && (citations.cited_by_count > 0 || citations.references_count > 0) && (
            <div className="citation-network" style={{ marginTop: 24, padding: '12px 0', borderTop: '1px solid #eee' }}>
              <h3 style={{ fontSize: 13, marginBottom: 8, color: '#555' }}>Citation Network in JournalFeeds</h3>
              {citations.cited_by_count > 0 && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 'bold', color: '#888', marginBottom: 4 }}>Cited by {citations.cited_by_count} paper{citations.cited_by_count !== 1 ? 's' : ''}</div>
                  {citations.cited_by.map((a: any) => (
                    <div key={a.id} style={{ fontSize: 12, marginBottom: 3 }}>
                      <Link to={`/jf/article/${a.slug}`} style={{ color: '#5f99cf' }}>{a.title}</Link>
                      <span style={{ color: '#999', fontSize: 10, marginLeft: 6 }}>{a.publisher}</span>
                    </div>
                  ))}
                </div>
              )}
              {citations.references_count > 0 && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 'bold', color: '#888', marginBottom: 4 }}>References {citations.references_count} paper{citations.references_count !== 1 ? 's' : ''} in JournalFeeds</div>
                  {citations.references.map((a: any) => (
                    <div key={a.id} style={{ fontSize: 12, marginBottom: 3 }}>
                      <Link to={`/jf/article/${a.slug}`} style={{ color: '#5f99cf' }}>{a.title}</Link>
                      <span style={{ color: '#999', fontSize: 10, marginLeft: 6 }}>{a.publisher}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div ref={commentsRef} id="comments" style={{ marginTop: '30px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
            <h3 style={{ fontSize: 14, marginBottom: 12 }}>Comments ({comments.length})</h3>

            {token ? (
              <div style={{ marginBottom: 20 }}>
                <textarea value={newComment} onChange={e => setNewComment(e.target.value)}
                  placeholder="What are your thoughts?"
                  style={{ width: '100%', height: 60, fontSize: 13, padding: 8, boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: 4 }} />
                <button onClick={() => postComment()} className="create-post-btn" style={{ marginTop: 6, fontSize: 12, padding: '4px 14px' }}>comment</button>
              </div>
            ) : (
              <p style={{ fontSize: 12, color: '#888', marginBottom: 16 }}>
                <button onClick={() => setShowLogin(true)} className="link-btn" style={{ color: '#5f99cf', textDecoration: 'underline' }}>Log in</button> to join the discussion.
              </p>
            )}

            {comments.length === 0 && <p style={{ fontSize: 12, color: '#888' }}>No comments yet. Be the first to discuss this article.</p>}
            {comments.map(c => renderComment(c))}
          </div>
        </div>
      </div>
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// Feed Page (home + category browsing)
// ---------------------------------------------------------------------------

function findCatName(cats: Category[], slug: string): string | null {
  for (const cat of cats) {
    if (cat.slug === slug) return cat.name;
    if (cat.subcategories) {
      const found = findCatName(cat.subcategories, slug);
      if (found) return found;
    }
  }
  return null;
}

function findCatId(cats: Category[], slug: string): number | null {
  for (const cat of cats) {
    if (cat.slug === slug) return cat.id;
    if (cat.subcategories) {
      const found = findCatId(cat.subcategories, slug);
      if (found) return found;
    }
  }
  return null;
}

function ReadStatusButton({ articleId, token, user, setShowLogin }: { articleId: number; token: string | null; user: any; setShowLogin: (v: boolean) => void }) {
  const readIds: number[] = user?.read_articles || [];
  const wantIds: number[] = user?.want_to_read_articles || [];
  const isRead = readIds.includes(articleId);
  const isWant = wantIds.includes(articleId);
  const [showPrivacyPrompt, setShowPrivacyPrompt] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<string | null>(null);

  const setStatus = async (status: string, listPublic?: boolean) => {
    if (!token) { setShowLogin(true); return; }
    // Check if lists exist yet — if not, prompt for privacy
    if (!user?.read_settings && status !== 'none') {
      setPendingStatus(status);
      setShowPrivacyPrompt(true);
      return;
    }
    await fetch(`/api/article/${articleId}/read-status`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ status, list_public: listPublic }),
    });
    // Refresh user data
    const res = await fetch('/api/profile', { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) {
      const data = await res.json();
      Object.assign(user, data);
    }
  };

  const confirmPrivacy = async (isPublic: boolean) => {
    setShowPrivacyPrompt(false);
    if (pendingStatus) {
      user.read_settings = { read: isPublic, 'want-to-read': isPublic };
      await setStatus(pendingStatus, isPublic);
      setPendingStatus(null);
    }
  };

  return (
    <span style={{ position: 'relative' }}>
      <button
        onClick={() => setStatus(isRead ? 'none' : 'read')}
        className="link-btn"
        title={isRead ? 'Remove from Read' : 'Mark as Read'}
        style={{ color: isRead ? '#228B22' : undefined }}
      >
        {isRead ? '✓ read' : '✓'}
      </button>
      <button
        onClick={() => setStatus(isWant ? 'none' : 'want-to-read')}
        className="link-btn"
        title={isWant ? 'Remove from Want to Read' : 'Want to Read'}
        style={{ color: isWant ? '#DAA520' : undefined }}
      >
        {isWant ? '★ later' : '☆'}
      </button>
      {showPrivacyPrompt && (
        <div className="save-dropdown" style={{ width: 200, left: 0 }}>
          <div style={{ fontSize: 11, marginBottom: 6 }}>Make your reading lists public or private?</div>
          <div className="save-dropdown-item" onClick={() => confirmPrivacy(false)} style={{ fontWeight: 'bold' }}>Private (only you)</div>
          <div className="save-dropdown-item" onClick={() => confirmPrivacy(true)}>Public (visible to all)</div>
          <div className="save-dropdown-item" onClick={() => { setShowPrivacyPrompt(false); setPendingStatus(null); }} style={{ color: '#888' }}>cancel</div>
        </div>
      )}
    </span>
  );
}

function FeedPage(props: any) {
  const { token, user, setShowLogin, setUser, categories: allCategories, api: apiProp } = props;
  const i18n = useI18n();
  const api = apiProp || ((u: string) => localizeApiUrl(u, i18n.apiLang));
  const t = props.t || i18n.t;
  const { cat1, cat2, cat3, cat4 } = useParams();
  const [searchParams] = useSearchParams();
  const activeSearch = searchParams.get('q') || '';
  const sort = searchParams.get('sort') || 'hottest';
  const [articles, setArticles] = useState<Article[]>([]);
  const [activeComments, setActiveComments] = useState<number | null>(null);
  const [comments, setComments] = useState<any[]>([]);
  const [newComment, setNewComment] = useState('');
  const [dropdownArticle, setDropdownArticle] = useState<number | null>(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [categoryNotFound, setCategoryNotFound] = useState(false);
  const oaOnly = searchParams.get('oa') === '1';
  const nextPageCache = useRef<Article[] | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const currentSlugPath = [cat1, cat2, cat3, cat4].filter(Boolean).join('/');

  const buildUrl = useCallback((p: number) => {
    const params = new URLSearchParams();
    if (currentSlugPath) params.append('slug', currentSlugPath);
    if (activeSearch) params.append('q', activeSearch);
    params.append('sort', sort);
    params.append('page', String(p));
    if (oaOnly) params.append('oa', '1');
    return api(`/api/articles?${params.toString()}`);
  }, [currentSlugPath, activeSearch, sort, oaOnly, api]);

  const authHeaders = useCallback((): Record<string, string> => {
    const h: Record<string, string> = {};
    if (token) h['Authorization'] = `Bearer ${token}`;
    return h;
  }, [token]);

  // Pre-fetch next page
  const prefetchNext = useCallback((currentPage: number, currentHasMore: boolean) => {
    if (!currentHasMore) return;
    nextPageCache.current = null;
    fetch(buildUrl(currentPage + 1), { headers: authHeaders() })
      .then(r => r.json())
      .then(data => { nextPageCache.current = data.articles || []; });
  }, [buildUrl, authHeaders]);

  // Initial load and reset on navigation
  useEffect(() => {
    setPage(1);
    setArticles([]);
    setHasMore(false);
    nextPageCache.current = null;

    setCategoryNotFound(false);
    fetch(buildUrl(1), { headers: authHeaders() })
      .then(res => {
        if (res.status === 404) {
          return res.json().then(data => {
            if (data.category_not_found) setCategoryNotFound(true);
          });
        }
        return res.json().then(data => {
          setArticles(data.articles || []);
          setHasMore(data.has_more || false);
          prefetchNext(1, data.has_more || false);
        });
      });
  }, [currentSlugPath, activeSearch, token, buildUrl, authHeaders, prefetchNext]);

  // Check if user follows the current category
  const currentSlug = cat4 || cat3 || cat2 || cat1 || null;
  const currentCatId = currentSlug ? findCatId(allCategories, currentSlug) : null;

  useEffect(() => {
    if (!token || !currentCatId) { setIsFollowing(false); return; }
    fetch('/api/subscriptions', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => setIsFollowing(data.category_ids?.includes(currentCatId) || false));
  }, [token, currentCatId]);

  const toggleFollow = async () => {
    if (!token) return props.setShowLogin(true);
    if (!currentCatId) return;
    setFollowLoading(true);
    const res = await fetch(`/api/subscriptions/toggle-category/${currentCatId}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    setIsFollowing(data.status === 'followed');
    setFollowLoading(false);
  };

  // Load more when sentinel is visible
  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    const nextPage = page + 1;

    const applyPage = (data: { articles: Article[]; has_more: boolean }) => {
      setArticles(prev => {
        const ids = new Set(prev.map(a => a.id));
        const fresh = data.articles.filter(a => !ids.has(a.id));
        return [...prev, ...fresh];
      });
      setPage(nextPage);
      setHasMore(data.has_more || false);
      setLoadingMore(false);
      prefetchNext(nextPage, data.has_more || false);
    };

    if (nextPageCache.current) {
      applyPage({ articles: nextPageCache.current, has_more: true });
      // We assumed has_more=true from cache; re-fetch to get actual value
      fetch(buildUrl(nextPage), { headers: authHeaders() })
        .then(r => r.json())
        .then(data => setHasMore(data.has_more || false));
    } else {
      fetch(buildUrl(nextPage), { headers: authHeaders() })
        .then(r => r.json())
        .then(applyPage);
    }
  }, [loadingMore, hasMore, page, buildUrl, authHeaders, prefetchNext]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMore(); },
      { rootMargin: '400px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore]);

  const handleVote = async (articleId: number, type: number) => {
    if (!token) return setShowLogin(true);
    await fetch(`/api/article/${articleId}/vote`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ vote_type: type }),
    });
    setArticles(articles.map(a => {
      if (a.id !== articleId) return a;
      const prev = a.user_vote || 0;
      let newVote: number;
      let delta: number;
      if (prev === type) {
        // Same arrow clicked again — remove vote
        newVote = 0;
        delta = -type;
      } else {
        // New vote or switching direction
        newVote = type;
        delta = type - prev;
      }
      return { ...a, vote_count: a.vote_count + delta, user_vote: newVote };
    }));
  };

  const handleSave = async (articleId: number) => {
    if (!token) return setShowLogin(true);
    await fetch(`/api/article/${articleId}/save`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    const res = await fetch('/api/profile', { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return;
    setUser(await res.json());
  };

  const fetchComments = async (articleId: number) => {
    const res = await fetch(`/api/article/${articleId}/comments`);
    setComments(await res.json());
    setActiveComments(articleId);
  };

  const postComment = async (articleId: number) => {
    if (!token) return setShowLogin(true);
    await fetch(`/api/article/${articleId}/comments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ content: newComment }),
    });
    setNewComment('');
    fetchComments(articleId);
  };

  const currentCatName = currentSlug ? (findCatName(allCategories, currentSlug) || currentSlug.replace(/-/g, ' ')) : null;

  if (categoryNotFound) {
    return <Layout {...props}><NotFoundPage /></Layout>;
  }

  return (
    <Layout {...props}>
      {currentCatName && (
        <div className="category-banner">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div className="category-banner-title">jf/{currentCatName}</div>
            <button
              onClick={toggleFollow}
              disabled={followLoading}
              className={isFollowing ? 'follow-btn following' : 'follow-btn'}
            >
              {followLoading ? '...' : isFollowing ? 'Following' : 'Follow'}
            </button>
          </div>
          <div className="category-banner-path">
            {[cat1, cat2, cat3, cat4].filter(Boolean).map((slug, i, arr) => {
              const name = findCatName(allCategories, slug!) || slug!.replace(/-/g, ' ');
              return (
                <span key={slug}>
                  {i > 0 && ' › '}
                  <Link to={'/jf/' + arr.slice(0, i + 1).join('/')}>{name}</Link>
                </span>
              );
            })}
          </div>
        </div>
      )}
      {token && (
        <div style={{ marginBottom: 12, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <Link to="/jf/submit" className="create-post-btn">{t.feed_create_post}</Link>
          {activeSearch && (
            <button
              className="create-post-btn"
              style={{ background: '#5f99cf' }}
              onClick={async () => {
                const name = prompt('Name this saved search:', activeSearch);
                if (name === null) return;
                const res = await fetch('/api/saved-searches', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                  body: JSON.stringify({ query: activeSearch, category_slug: currentSlugPath || null, name: name || activeSearch }),
                });
                if (res.status === 201) alert('Search saved!');
                else if (res.status === 409) alert('This search is already saved.');
                else alert('Failed to save search.');
              }}
            >
              &#128269; Save this search
            </button>
          )}
        </div>
      )}
      {articles.map((art, index) => (
        <div key={art.id} className="article-row">
          <div className="rank">{index + 1}</div>
          <div className="vote-arrows">
            <div className={`arrow${art.user_vote === 1 ? ' arrow-active-up' : ''}`} onClick={() => handleVote(art.id, 1)}>&#9650;</div>
            <div className={`arrow${art.user_vote === -1 ? ' arrow-active-down' : ''}`} onClick={() => handleVote(art.id, -1)}>&#9660;</div>
          </div>
          <div className="article-body">
            <div className="article-title">
              {art.is_open_access && <span className="oa-badge" title="Open Access">OA</span>}
              <Link to={`/jf/article/${art.slug}`} dangerouslySetInnerHTML={{__html: art.title}} />
              <span className="domain">({art.publisher})</span>
            </div>
            <div className="meta-line">
              {t.article_submitted_by} <b>knowsyn</b> {timeAgo(art.pub_date)} |
              <b>{art.vote_count} {t.article_points}</b> | {token
                ? <Link to={`/jf/article/${art.slug}/jfif`} className="jfif-badge jfif-link" title="View JFIF breakdown">JFIF: {art.jfif.toFixed(1)}</Link>
                : <Link to="/about#jfif" className="jfif-badge jfif-link" title="Log in to see calculation">JFIF: {art.jfif.toFixed(1)}</Link>
              } |{' '}
              <Link to={`/jf/article/${art.slug}#comments`} className="link-btn">{t.article_comments}</Link> |{' '}
              <button onClick={() => handleSave(art.id)} className="link-btn">
                {user?.saved_articles?.includes(art.id) ? t.article_unsave : t.article_save}
              </button>
              {' '}| <ReadStatusButton articleId={art.id} token={token} user={user} setShowLogin={setShowLogin} />
              {token && (
                <span style={{ position: 'relative' }}>
                  {' '}
                  <button onClick={() => setDropdownArticle(dropdownArticle === art.id ? null : art.id)}
                    className="link-btn" style={{ fontSize: 9 }}>&#9660;</button>
                  {dropdownArticle === art.id && (
                    <SaveToListDropdown articleId={art.id} token={token} onClose={() => setDropdownArticle(null)} />
                  )}
                </span>
              )}
            </div>
          </div>
        </div>
      ))}
      {hasMore && (
        <div ref={sentinelRef} style={{ textAlign: 'center', padding: '20px 0', fontSize: 11, color: '#888' }}>
          {loadingMore ? 'loading more...' : ''}
        </div>
      )}
      {!hasMore && articles.length > 0 && (
        <div style={{ textAlign: 'center', padding: '10px 0', fontSize: 11, color: '#888' }}>
          end of results
        </div>
      )}
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// Category Sidebar Item
// ---------------------------------------------------------------------------
// Submit Post Page
// ---------------------------------------------------------------------------

function NotFoundPage() {
  const quotes = [
    { text: "Not all those who wander are lost — but you definitely are.", author: "Adapted from J.R.R. Tolkien" },
    { text: "I have not failed. I've just found a URL that doesn't work.", author: "Adapted from Thomas Edison" },
    { text: "The only true wisdom is knowing this page doesn't exist.", author: "Adapted from Socrates" },
    { text: "In the middle of difficulty lies opportunity... but not this page.", author: "Adapted from Albert Einstein" },
    { text: "Science is a way of thinking much more than a body of knowledge. This URL, however, is neither.", author: "Adapted from Carl Sagan" },
    { text: "The important thing is not to stop questioning. Except why you typed that URL.", author: "Adapted from Albert Einstein" },
    { text: "Peer review rejected this page.", author: "Anonymous Reviewer #2" },
    { text: "Results: page not found (p < 0.001, n = 1).", author: "Statistical Analysis" },
  ];
  const quote = quotes[Math.floor(Math.random() * quotes.length)];
  return (
    <div style={{ textAlign: 'center', padding: '80px 20px', maxWidth: 600, margin: '0 auto' }}>
      <h1 style={{ fontSize: 72, margin: '0 0 10px', color: '#ccc' }}>404</h1>
      <h2 style={{ margin: '0 0 30px', color: '#555' }}>Page Not Found</h2>
      <blockquote style={{ fontStyle: 'italic', fontSize: 18, lineHeight: 1.6, color: '#666', margin: '0 0 10px' }}>
        "{quote.text}"
      </blockquote>
      <p style={{ color: '#999', fontSize: 14 }}>— {quote.author}</p>
      <div style={{ marginTop: 40 }}>
        <Link to="/" style={{ color: '#5f99cf', marginRight: 20 }}>Home</Link>
        <Link to="/jf" style={{ color: '#5f99cf', marginRight: 20 }}>Browse Articles</Link>
        <Link to="/jf/trending" style={{ color: '#5f99cf' }}>Trending</Link>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function JFIFBreakdownPage({ token }: { token: string }) {
  const { slug } = useParams<{ slug: string }>();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    // First get article ID from slug
    fetch(`/api/article/${slug}`)
      .then(r => r.json())
      .then(art => {
        if (!art.id) { setLoading(false); return; }
        return fetch(`/api/article/${art.id}/jfif`, {
          headers: { Authorization: `Bearer ${token}` },
        }).then(r => r.json()).then(jfif => {
          setData({ ...jfif, article: art });
          setLoading(false);
        });
      })
      .catch(() => setLoading(false));
  }, [slug, token]);

  if (loading) return <div className="feed-page" style={{ padding: 20 }}>Loading JFIF breakdown...</div>;
  if (!data || !data.breakdown) return <div className="feed-page" style={{ padding: 20 }}>No JFIF data available.</div>;

  const b = data.breakdown;
  const rows = [
    { label: 'Upvotes', detail: `${b.votes.count} votes × ${b.votes.weight}`, points: b.votes.points },
    { label: 'Comments', detail: `${b.comments.count} comments × ${b.comments.weight}`, points: b.comments.points },
    { label: 'Saves', detail: `${b.saves.count} saves × ${b.saves.weight}`, points: b.saves.points },
    { label: 'JF Story Links', detail: `${b.msm_internal.count} stories × ${b.msm_internal.weight}`, points: b.msm_internal.points },
    { label: 'In the News (prestige)', detail: `${b.in_the_news_prestige?.count || 0} mentions × ${b.in_the_news_prestige?.weight || 8}`, points: b.in_the_news_prestige?.points || 0 },
    { label: 'In the News (aggregator)', detail: `${b.in_the_news_aggregator?.count || 0} mentions × ${b.in_the_news_aggregator?.weight || 2}`, points: b.in_the_news_aggregator?.points || 0 },
    { label: 'Social Mentions', detail: `${b.social.count} mentions × ${b.social.weight}`, points: b.social.points },
    { label: 'Open Access Bonus', detail: b.oa_bonus.is_oa ? 'Yes — CC-licensed' : 'Not OA', points: b.oa_bonus.points },
    { label: 'Journal Influence*', detail: b.impact_factor.value ? `IF ${b.impact_factor.value} × ${b.impact_factor.weight}` : 'N/A', points: b.impact_factor.points },
  ];
  if (b.editorial_penalty?.is_editorial) {
    rows.push({ label: 'Editorial Penalty', detail: 'Comment/Letter/Editorial', points: b.editorial_penalty.points });
  }

  return (
    <div className="feed-page" style={{ padding: 20, maxWidth: 700 }}>
      <h2 style={{ marginBottom: 4 }}>JFIF Score Breakdown</h2>
      <p style={{ color: '#666', fontSize: 13, marginBottom: 16 }}>
        <Link to={`/jf/article/${slug}`} style={{ color: '#5f99cf' }}>{data.article_title || data.article?.title}</Link>
        {' '}— {data.article_publisher || data.article?.publisher}
      </p>

      <div style={{ fontSize: 28, fontWeight: 'bold', color: '#ff4500', marginBottom: 20 }}>
        JFIF: {data.score.toFixed(1)}
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #ccc', textAlign: 'left' }}>
            <th style={{ padding: '8px 4px' }}>Signal</th>
            <th style={{ padding: '8px 4px' }}>Detail</th>
            <th style={{ padding: '8px 4px', textAlign: 'right' }}>Points</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
              <td style={{ padding: '6px 4px', fontWeight: 500 }}>{r.label}</td>
              <td style={{ padding: '6px 4px', color: '#666' }}>{r.detail}</td>
              <td style={{ padding: '6px 4px', textAlign: 'right', fontWeight: r.points !== 0 ? 'bold' : 'normal', color: r.points > 0 ? '#228B22' : r.points < 0 ? '#ff4500' : '#999' }}>
                {r.points > 0 ? '+' : ''}{r.points}
              </td>
            </tr>
          ))}
          <tr style={{ borderTop: '2px solid #333' }}>
            <td style={{ padding: '8px 4px', fontWeight: 'bold' }}>Total</td>
            <td></td>
            <td style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 'bold', fontSize: 16 }}>{data.score.toFixed(1)}</td>
          </tr>
        </tbody>
      </table>

      <p style={{ color: '#999', fontSize: 11, marginTop: 16 }}>
        Last updated: {data.updated_at ? new Date(data.updated_at).toLocaleString() : 'N/A'}.
        JFIF is recomputed nightly. <Link to="/about#jfif" style={{ color: '#5f99cf' }}>Learn more about JFIF</Link>.
      </p>
      <p style={{ color: '#999', fontSize: 11, marginTop: 8, fontStyle: 'italic' }}>
        * Journal Influence is a small component based on publicly available citation metrics for each journal.
        It provides a minor baseline signal reflecting the journal's general reach, but is deliberately weighted
        low (0.05 points per unit) so that actual public discourse — news coverage, social mentions,
        community engagement — drives the vast majority of any article's JFIF score.
      </p>
    </div>
  );
}


function TrendingPage(props: any) {
  const { token, user, setShowLogin, setUser, api: apiProp } = props;
  const i18n = useI18n();
  const { t } = i18n;
  const api = apiProp || ((u: string) => localizeApiUrl(u, i18n.apiLang));
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [dropdownArticle, setDropdownArticle] = useState<number | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const handleVote = async (articleId: number, voteType: number) => {
    if (!token) { setShowLogin(true); return; }
    const res = await fetch(`/api/article/${articleId}/vote`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ vote_type: voteType }),
    });
    if (res.ok) {
      const data = await res.json();
      setArticles(prev => prev.map(a => a.id === articleId ? { ...a, vote_count: data.vote_count, user_vote: data.user_vote } : a));
    }
  };

  const handleSave = async (articleId: number) => {
    if (!token) { setShowLogin(true); return; }
    const res = await fetch(`/api/article/${articleId}/save`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok && user && setUser) {
      const saved = user.saved_articles || [];
      setUser({ ...user, saved_articles: saved.includes(articleId) ? saved.filter((id: number) => id !== articleId) : [...saved, articleId] });
    }
  };

  const authHeaders = useCallback((): Record<string, string> => {
    const h: Record<string, string> = {};
    if (token) h['Authorization'] = `Bearer ${token}`;
    return h;
  }, [token]);

  // Initial load
  useEffect(() => {
    setLoading(true);
    setArticles([]);
    setPage(1);
    fetch(api('/api/trending?page=1'), { headers: authHeaders() })
      .then(r => r.json())
      .then(data => {
        setArticles(data.articles || []);
        setHasMore(data.has_more || false);
        setLoading(false);
      });
  }, [token, authHeaders]);

  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    const nextPage = page + 1;
    fetch(api(`/api/trending?page=${nextPage}`), { headers: authHeaders() })
      .then(r => r.json())
      .then(data => {
        setArticles(prev => {
          const ids = new Set(prev.map(a => a.id));
          const fresh = (data.articles || []).filter((a: Article) => !ids.has(a.id));
          return [...prev, ...fresh];
        });
        setPage(nextPage);
        setHasMore(data.has_more || false);
        setLoadingMore(false);
      });
  }, [loadingMore, hasMore, page, authHeaders]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMore(); },
      { rootMargin: '400px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore]);

  return (
    <Layout {...props}>
      <h2 style={{ fontSize: 16, marginBottom: 12 }}>Trending Research <span style={{ fontSize: 11, color: '#888', fontWeight: 'normal' }}>sorted by JFIF score</span></h2>
      {loading ? <div>Loading...</div> : articles.length === 0 ? <div style={{ color: '#888', fontSize: 13 }}>No trending articles yet. JFIF scores are computed nightly.</div> : (
        <>
          {articles.map((art, i) => (
            <div key={art.id} className="article-row">
              <div className="rank">{i + 1}</div>
              <div className="vote-arrows">
                <div className={`arrow${art.user_vote === 1 ? ' arrow-active-up' : ''}`} onClick={() => handleVote(art.id, 1)}>&#9650;</div>
                <div className={`arrow${art.user_vote === -1 ? ' arrow-active-down' : ''}`} onClick={() => handleVote(art.id, -1)}>&#9660;</div>
              </div>
              <div className="article-body">
                <div className="article-title">
                  {art.is_open_access && <span className="oa-badge" title="Open Access">OA</span>}
                  <Link to={`/jf/article/${art.slug}`} dangerouslySetInnerHTML={{__html: art.title}} />
                  <span className="domain">({art.publisher})</span>
                </div>
                <div className="meta-line">
                  {t.article_submitted_by} <b>knowsyn</b> {timeAgo(art.pub_date)} |
                  <b>{art.vote_count} {t.article_points}</b> | {token
                    ? <Link to={`/jf/article/${art.slug}/jfif`} className="jfif-badge jfif-link" title="View JFIF breakdown">JFIF: {art.jfif.toFixed(1)}</Link>
                    : <Link to="/about#jfif" className="jfif-badge jfif-link" title="Log in to see calculation">JFIF: {art.jfif.toFixed(1)}</Link>
                  } |{' '}
                  <Link to={`/jf/article/${art.slug}#comments`} className="link-btn">{t.article_comments}</Link> |{' '}
                  <button onClick={() => handleSave(art.id)} className="link-btn">
                    {user?.saved_articles?.includes(art.id) ? t.article_unsave : t.article_save}
                  </button>
                  {' '}| <ReadStatusButton articleId={art.id} token={token} user={user} setShowLogin={setShowLogin} />
                  {token && (
                    <span style={{ position: 'relative' }}>
                      {' '}
                      <button onClick={() => setDropdownArticle(dropdownArticle === art.id ? null : art.id)}
                        className="link-btn" style={{ fontSize: 9 }}>&#9660;</button>
                      {dropdownArticle === art.id && (
                        <SaveToListDropdown articleId={art.id} token={token} onClose={() => setDropdownArticle(null)} />
                      )}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
          {hasMore && (
            <div ref={sentinelRef} style={{ textAlign: 'center', padding: '20px 0', fontSize: 11, color: '#888' }}>
              {loadingMore ? 'loading more...' : ''}
            </div>
          )}
          {!hasMore && articles.length > 0 && (
            <div style={{ textAlign: 'center', padding: '10px 0', fontSize: 11, color: '#888' }}>
              end of results
            </div>
          )}
        </>
      )}
    </Layout>
  );
}

// ---------------------------------------------------------------------------

function SubmitPostPage({ token, categories }: { token: string | null; categories: Category[] }) {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [link, setLink] = useState('');
  const [summary, setSummary] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const flatCategories = (cats: Category[], depth = 0): { id: number; name: string; depth: number }[] => {
    const result: { id: number; name: string; depth: number }[] = [];
    for (const cat of cats) {
      result.push({ id: cat.id, name: cat.name, depth });
      if (cat.subcategories) result.push(...flatCategories(cat.subcategories, depth + 1));
    }
    return result;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch('/api/articles/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ title, link, summary, category_id: parseInt(categoryId) }),
      });
      const data = await res.json();
      if (res.ok) {
        navigate(`/jf/article/${data.slug}`);
      } else {
        setError(data.message || 'Failed to submit.');
      }
    } catch {
      setError('Network error.');
    }
    setSubmitting(false);
  };

  if (!token) return <div style={{ padding: 40, textAlign: 'center', fontSize: 13 }}>You must be logged in to submit a post.</div>;

  return (
    <div style={{ maxWidth: 600, margin: '0 auto', padding: 20 }}>
      <h2 style={{ fontSize: 16, marginBottom: 16 }}>Submit a Post</h2>
      {error && <div style={{ color: '#ff4500', fontSize: 12, marginBottom: 10 }}>{error}</div>}
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 'bold', display: 'block', marginBottom: 4 }}>Title *</label>
          <input value={title} onChange={e => setTitle(e.target.value)} required
            style={{ width: '100%', padding: '6px 8px', border: '1px solid #ccc', fontSize: 13, boxSizing: 'border-box' }} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 'bold', display: 'block', marginBottom: 4 }}>URL *</label>
          <input value={link} onChange={e => setLink(e.target.value)} type="url" required
            style={{ width: '100%', padding: '6px 8px', border: '1px solid #ccc', fontSize: 13, boxSizing: 'border-box' }} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 'bold', display: 'block', marginBottom: 4 }}>Summary</label>
          <textarea value={summary} onChange={e => setSummary(e.target.value)} rows={4}
            style={{ width: '100%', padding: '6px 8px', border: '1px solid #ccc', fontSize: 13, boxSizing: 'border-box' }} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 'bold', display: 'block', marginBottom: 4 }}>Category *</label>
          <select value={categoryId} onChange={e => setCategoryId(e.target.value)} required
            style={{ width: '100%', padding: '6px 8px', border: '1px solid #ccc', fontSize: 13 }}>
            <option value="">Select a category</option>
            {flatCategories(categories).map(c => (
              <option key={c.id} value={c.id}>{'  '.repeat(c.depth) + c.name}</option>
            ))}
          </select>
        </div>
        <button type="submit" disabled={submitting} className="create-post-btn" style={{ padding: '8px 20px', fontSize: 13 }}>
          {submitting ? 'Submitting...' : 'Submit'}
        </button>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------

function CategoryItem({ cat, currentPath, depth = 0 }: { cat: Category; currentPath: string; depth?: number }) {
  const newPath = currentPath ? `${currentPath}/${cat.slug}` : `/jf/${cat.slug}`;
  return (
    <div style={{ marginLeft: depth > 0 ? '10px' : '0', marginBottom: '5px' }}>
      <Link
        to={newPath}
        style={{
          textDecoration: 'none',
          color: window.location.pathname === newPath ? '#ff4500' : 'inherit',
          fontSize: depth === 0 ? '13px' : '12px',
          fontWeight: depth === 0 ? 'bold' : 'normal'
        }}
      >
        {cat.name.toLowerCase()}
      </Link>
      {cat.subcategories && cat.subcategories.map((sub: any) => (
        <CategoryItem key={sub.id} cat={sub} currentPath={newPath} depth={depth + 1} />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Email Verification Landing
// ---------------------------------------------------------------------------

function VerifyEmail({ onLogin }: { onLogin: (token: string, user: any) => void }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) { setStatus('error'); setMessage('Missing verification token.'); return; }

    fetch(`/api/auth/verify?token=${token}`)
      .then(r => r.json().then(data => ({ ok: r.ok, data })))
      .then(({ ok, data }) => {
        if (ok) {
          setStatus('success');
          setMessage('Email verified! Redirecting...');
          onLogin(data.access_token, { username: data.username, onboarding_complete: data.onboarding_complete });
          setTimeout(() => navigate('/'), 1500);
        } else {
          setStatus('error');
          setMessage(data.message || 'Verification failed.');
        }
      })
      .catch(() => { setStatus('error'); setMessage('Network error.'); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ padding: 40, textAlign: 'center' }}>
      <h2 style={{ fontSize: 16 }}>email verification</h2>
      <p style={{ fontSize: 13, color: status === 'error' ? '#ff4500' : '#888' }}>{message || 'Verifying...'}</p>
    </div>
  );
}

function ReadingTab({ token, profile, setProfile }: { token: string; profile: any; setProfile: (p: any) => void }) {
  const [readItems, setReadItems] = useState<any[]>([]);
  const [wantItems, setWantItems] = useState<any[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [editingNote, setEditingNote] = useState<number | null>(null);
  const [editText, setEditText] = useState('');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  useEffect(() => {
    // Fetch the actual lists with items+notes
    const fetchList = async (slug: string) => {
      try {
        const res = await fetch(`/api/lists/${slug}`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return [];
        const data = await res.json();
        return data.items || [];
      } catch { return []; }
    };
    Promise.all([
      fetchList(`${profile.username}-read`),
      fetchList(`${profile.username}-want-to-read`),
    ]).then(([r, w]) => { setReadItems(r); setWantItems(w); setLoaded(true); });
  }, [token, profile.username]);

  const removeItem = async (itemId: number, listType: string) => {
    const slug = `${profile.username}-${listType}`;
    const bl = await fetch(`/api/lists/${slug}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json());
    await fetch(`/api/lists/${bl.id}/items/${itemId}`, { method: 'DELETE', headers });
    if (listType === 'read') setReadItems(prev => prev.filter(i => i.id !== itemId));
    else setWantItems(prev => prev.filter(i => i.id !== itemId));
  };

  const saveNote = async (itemId: number) => {
    await fetch(`/api/notes/${itemId}`, { method: 'PUT', headers, body: JSON.stringify({ note: editText }) });
    setEditingNote(null);
    const update = (items: any[]) => items.map(i => i.id === itemId ? { ...i, note: editText } : i);
    setReadItems(update);
    setWantItems(update);
  };

  const togglePrivacy = async (listType: string) => {
    const current = profile.read_settings?.[listType] ?? false;
    await fetch('/api/read-list-settings', {
      method: 'PUT', headers,
      body: JSON.stringify({ [listType]: { is_public: !current } }),
    });
    setProfile({ ...profile, read_settings: { ...profile.read_settings, [listType]: !current } });
  };

  const renderItems = (items: any[], listType: string) => items.map((i: any) => (
    <div key={i.id} style={{ marginBottom: 10, fontSize: 12, borderBottom: '1px solid #eee', paddingBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Link to={`/jf/article/${i.article?.slug || ''}`} style={{ color: '#0000ff' }}>{i.article?.title}</Link>
        <button onClick={() => removeItem(i.id, listType)} className="link-btn" style={{ color: '#ff4500', fontSize: 10, flexShrink: 0 }}>remove</button>
      </div>
      {i.note && editingNote !== i.id && (
        <div style={{ marginTop: 4, padding: '4px 8px', background: '#f8f8f0', borderLeft: '3px solid #DAA520', fontSize: 12 }}>
          {i.note}
          <button onClick={() => { setEditingNote(i.id); setEditText(i.note); }} className="link-btn" style={{ marginLeft: 8, fontSize: 10 }}>edit</button>
        </div>
      )}
      {editingNote === i.id && (
        <div style={{ marginTop: 4 }}>
          <textarea value={editText} onChange={e => setEditText(e.target.value)} maxLength={5000}
            style={{ width: '100%', height: 60, fontSize: 12, padding: 6, boxSizing: 'border-box', border: '1px solid #ccc' }} />
          <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
            <button onClick={() => saveNote(i.id)} className="create-post-btn" style={{ fontSize: 10, padding: '2px 8px' }}>save</button>
            <button onClick={() => setEditingNote(null)} className="link-btn" style={{ fontSize: 10 }}>cancel</button>
          </div>
        </div>
      )}
      {!i.note && editingNote !== i.id && (
        <button onClick={() => { setEditingNote(i.id); setEditText(''); }} className="link-btn" style={{ fontSize: 10, marginTop: 4 }}>+ add note</button>
      )}
    </div>
  ));

  if (!loaded) return <div style={{ fontSize: 12, color: '#888' }}>Loading...</div>;

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h3 style={{ fontSize: 13, margin: 0 }}>Read ({readItems.length})</h3>
          <label style={{ fontSize: 10, display: 'flex', alignItems: 'center', gap: 4, color: '#888' }}>
            <input type="checkbox" checked={profile.read_settings?.read ?? false} onChange={() => togglePrivacy('read')} />
            public
          </label>
        </div>
        {readItems.length === 0 ? <p style={{ color: '#888', fontSize: 12 }}>No articles marked as read yet.</p> : renderItems(readItems, 'read')}
      </div>

      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h3 style={{ fontSize: 13, margin: 0 }}>Want to Read ({wantItems.length})</h3>
          <label style={{ fontSize: 10, display: 'flex', alignItems: 'center', gap: 4, color: '#888' }}>
            <input type="checkbox" checked={profile.read_settings?.['want-to-read'] ?? false} onChange={() => togglePrivacy('want-to-read')} />
            public
          </label>
        </div>
        {wantItems.length === 0 ? <p style={{ color: '#888', fontSize: 12 }}>No articles in your reading list yet.</p> : renderItems(wantItems, 'want-to-read')}
      </div>
    </div>
  );
}

function SubscriptionsTab({ token, categories }: { token: string; categories: Category[] }) {
  const [subCatIds, setSubCatIds] = useState<Set<number>>(new Set());
  const [subFeedIds, setSubFeedIds] = useState<Set<number>>(new Set());
  const [allFeeds, setAllFeeds] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    Promise.all([
      fetch('/api/subscriptions', { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
      fetch('/api/feeds').then(r => r.json()),
    ]).then(([subs, feeds]) => {
      setSubCatIds(new Set(subs.category_ids));
      setSubFeedIds(new Set(subs.feed_ids));
      setAllFeeds(feeds);
      setLoaded(true);
    });
  }, [token]);

  const collectAllIds = (cat: Category): number[] => {
    let ids = [cat.id];
    for (const sub of cat.subcategories || []) ids = ids.concat(collectAllIds(sub));
    return ids;
  };

  const toggleCategory = (cat: Category) => {
    const next = new Set(subCatIds);
    if (next.has(cat.id)) {
      for (const id of collectAllIds(cat)) next.delete(id);
    } else {
      next.add(cat.id);
    }
    setSubCatIds(next);
  };

  const toggleFeed = (fid: number) => {
    const next = new Set(subFeedIds);
    next.has(fid) ? next.delete(fid) : next.add(fid);
    setSubFeedIds(next);
  };

  const save = async () => {
    setSaving(true);
    await fetch('/api/subscriptions', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ category_ids: Array.from(subCatIds), feed_ids: Array.from(subFeedIds) }),
    });
    setSaving(false);
  };

  const renderCatTree = (cats: Category[], depth = 0): React.ReactNode =>
    cats.map(cat => (
      <React.Fragment key={cat.id}>
        <label className="onboarding-checkbox" style={{ marginLeft: depth * 16 }}>
          <input type="checkbox" checked={subCatIds.has(cat.id)} onChange={() => toggleCategory(cat)} />
          <span>{cat.name}</span>
        </label>
        {cat.subcategories?.length > 0 && renderCatTree(cat.subcategories, depth + 1)}
      </React.Fragment>
    ));

  // Feeds whose category is selected
  const visibleFeeds = allFeeds.filter(f => subCatIds.has(f.category_id));

  if (!loaded) return <div style={{ fontSize: 12, color: '#888' }}>Loading subscriptions...</div>;

  return (
    <div>
      <p style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>
        Select categories and sources for your personalized feed:
      </p>
      <div className="checkbox-grid scrollable" style={{ maxHeight: 400 }}>
        {categories.map(root => (
          <div key={root.id} style={{ marginBottom: 12 }}>
            <label className="onboarding-checkbox" style={{ fontWeight: 'bold' }}>
              <input type="checkbox" checked={subCatIds.has(root.id)} onChange={() => toggleCategory(root)} />
              <span>{root.name}</span>
            </label>
            {root.subcategories?.length > 0 && renderCatTree(root.subcategories, 1)}
          </div>
        ))}
      </div>

      {visibleFeeds.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <p style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>Sources from selected categories:</p>
          <div className="checkbox-grid scrollable" style={{ maxHeight: 300 }}>
            {visibleFeeds.map((f: any) => (
              <label key={f.id} className="onboarding-checkbox">
                <input type="checkbox" checked={subFeedIds.has(f.id)} onChange={() => toggleFeed(f.id)} />
                <span>
                  {f.name}
                  {f.impact_factor && <span style={{ color: '#888', fontSize: 10, marginLeft: 4 }}>IF: {f.impact_factor}</span>}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        <button onClick={save} disabled={saving} className="create-post-btn" style={{ padding: '6px 16px', fontSize: 12 }}>
          {saving ? 'saving...' : 'save subscriptions'}
        </button>
      </div>
    </div>
  );
}

function ProfilePage({ token, categories }: { token: string | null; categories: Category[] }) {
  const [profile, setProfile] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'posts' | 'comments' | 'lists' | 'votes' | 'searches' | 'subscriptions' | 'reading'>('posts');
  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMsg, setResetMsg] = useState('');
  const [digestEnabled, setDigestEnabled] = useState(false);

  useEffect(() => {
    if (!token) return;
    fetch('/api/profile', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => { setProfile(data); setResetEmail(data.email || ''); });
    fetch('/api/profile/digest-preferences', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => setDigestEnabled(data.digest_enabled || false))
      .catch(() => {});
  }, [token]);

  if (!token) return <div style={{ padding: 40, textAlign: 'center', fontSize: 13 }}>Please log in.</div>;
  if (!profile) return <div style={{ padding: 40 }}>Loading...</div>;

  const handleResetPassword = async () => {
    const res = await fetch('/api/auth/forgot-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: resetEmail }),
    });
    if (res.ok) setResetMsg('Reset link sent to your email.');
    else setResetMsg('Failed to send reset link.');
  };

  const tabs: { key: typeof activeTab; label: string }[] = [
    { key: 'posts', label: `my posts (${profile.my_posts?.length || 0})` },
    { key: 'comments', label: `my comments (${profile.my_comments?.length || 0})` },
    { key: 'lists', label: `my lists (${profile.my_lists?.length || 0})` },
    { key: 'reading', label: `reading (${(profile.read_articles?.length || 0) + (profile.want_to_read_articles?.length || 0)})` },
    { key: 'votes', label: `my votes (${profile.my_votes?.length || 0})` },
    { key: 'searches', label: `saved searches (${profile.my_searches?.length || 0})` },
    { key: 'subscriptions', label: 'subscriptions' },
  ];

  return (
    <div style={{ maxWidth: 700, margin: '0 auto', padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, borderBottom: '1px solid #5f99cf', paddingBottom: 8 }}>
        <h2 style={{ margin: 0, fontSize: 16 }}>{profile.username}</h2>
        <span style={{ fontSize: 11, color: '#888' }}>{profile.email}</span>
      </div>

      <div style={{ fontSize: 12, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
          <input type="checkbox" checked={digestEnabled} onChange={async (e) => {
            const val = e.target.checked;
            setDigestEnabled(val);
            await fetch('/api/profile/digest-preferences', {
              method: 'PUT',
              headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({ digest_enabled: val }),
            });
          }} />
          Weekly research digest
        </label>
        <span style={{ fontSize: 10, color: '#888' }}>Personalized articles emailed weekly</span>
      </div>

      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #eee', marginBottom: 16 }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            className="nav-tab" style={{ fontWeight: activeTab === t.key ? 'bold' : 'normal', borderBottom: activeTab === t.key ? '2px solid #5f99cf' : 'none' }}>
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'posts' && (
        <div>
          {(profile.my_posts || []).length === 0 && <p style={{ color: '#888', fontSize: 12 }}>No posts yet.</p>}
          {(profile.my_posts || []).map((p: any) => (
            <div key={p.id} style={{ marginBottom: 8, fontSize: 12 }}>
              <Link to={`/jf/article/${p.slug}`} style={{ color: '#0000ff' }} dangerouslySetInnerHTML={{ __html: p.title }} />
              <span style={{ color: '#888', marginLeft: 8 }}>{p.pub_date ? new Date(p.pub_date).toLocaleDateString() : ''}</span>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'comments' && (
        <div>
          {(profile.my_comments || []).length === 0 && <p style={{ color: '#888', fontSize: 12 }}>No comments yet.</p>}
          {(profile.my_comments || []).map((c: any) => (
            <div key={c.id} style={{ marginBottom: 12, fontSize: 12, borderBottom: '1px solid #eee', paddingBottom: 8 }}>
              <div style={{ color: '#888', marginBottom: 4 }}>
                on <Link to={`/jf/article/${c.article_slug}`} style={{ color: '#5f99cf' }}>{c.article_title}</Link>
                <span style={{ marginLeft: 8 }}>{c.created_at ? new Date(c.created_at).toLocaleDateString() : ''}</span>
              </div>
              <div>{c.content}</div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'lists' && (
        <div>
          {(profile.my_lists || []).length === 0 && <p style={{ color: '#888', fontSize: 12 }}>No lists yet.</p>}
          {(profile.my_lists || []).map((l: any) => (
            <div key={l.id} style={{ marginBottom: 8, fontSize: 12 }}>
              <Link to={`/jf/list/${l.slug}`} style={{ color: '#0000ff' }}>{l.title}</Link>
              <span style={{ color: '#888', marginLeft: 8 }}>{l.item_count} items</span>
              <span style={{ color: l.is_public ? '#228B22' : '#888', marginLeft: 8, fontSize: 10 }}>{l.is_public ? 'public' : 'private'}</span>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'votes' && (
        <div>
          {(profile.my_votes || []).length === 0 && <p style={{ color: '#888', fontSize: 12 }}>No votes yet.</p>}
          {(profile.my_votes || []).map((v: any, i: number) => (
            <div key={i} style={{ marginBottom: 8, fontSize: 12 }}>
              <span style={{ color: v.vote_type === 1 ? '#228B22' : '#ff4500', marginRight: 6 }}>{v.vote_type === 1 ? '▲' : '▼'}</span>
              <Link to={`/jf/article/${v.slug}`} style={{ color: '#0000ff' }} dangerouslySetInnerHTML={{ __html: v.title }} />
            </div>
          ))}
        </div>
      )}

      {activeTab === 'searches' && (
        <div>
          {(profile.my_searches || []).length === 0 && <p style={{ color: '#888', fontSize: 12 }}>No saved searches yet. Search for something and click "Save this search".</p>}
          {(profile.my_searches || []).map((ss: any) => (
            <div key={ss.id} style={{ marginBottom: 10, fontSize: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee', paddingBottom: 8 }}>
              <div>
                <Link
                  to={ss.category_slug ? `/jf/${ss.category_slug}?q=${encodeURIComponent(ss.query)}` : `/jf?q=${encodeURIComponent(ss.query)}`}
                  style={{ color: '#0000ff', fontWeight: 'bold' }}
                >
                  {ss.name}
                </Link>
                {ss.category_slug && <span style={{ color: '#888', marginLeft: 8, fontSize: 10 }}>in /{ss.category_slug}</span>}
                <span style={{ color: '#888', marginLeft: 8, fontSize: 10 }}>{ss.created_at ? new Date(ss.created_at).toLocaleDateString() : ''}</span>
              </div>
              <button
                onClick={async () => {
                  if (!window.confirm('Delete this saved search?')) return;
                  await fetch(`/api/saved-searches/${ss.id}`, {
                    method: 'DELETE',
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  setProfile((prev: any) => ({
                    ...prev,
                    my_searches: prev.my_searches.filter((s: any) => s.id !== ss.id),
                  }));
                }}
                className="link-btn"
                style={{ color: '#ff4500', fontSize: 11 }}
              >
                delete
              </button>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'reading' && (
        <ReadingTab token={token!} profile={profile} setProfile={setProfile} />
      )}

      {activeTab === 'subscriptions' && (
        <SubscriptionsTab token={token!} categories={categories} />
      )}

      <div style={{ marginTop: 30, paddingTop: 16, borderTop: '1px solid #eee' }}>
        <button onClick={() => setShowReset(!showReset)}
          style={{ background: 'none', border: 'none', color: '#5f99cf', cursor: 'pointer', fontSize: 12, padding: 0, textDecoration: 'underline' }}>
          reset password
        </button>
        {showReset && (
          <div style={{ marginTop: 10 }}>
            <p style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>A reset link will be sent to: <b>{resetEmail}</b></p>
            <button onClick={handleResetPassword} className="create-post-btn" style={{ fontSize: 11, padding: '4px 10px' }}>send reset link</button>
            {resetMsg && <p style={{ fontSize: 11, color: '#228B22', marginTop: 6 }}>{resetMsg}</p>}
          </div>
        )}
      </div>

      {profile.is_admin && (
        <div style={{ marginTop: 16 }}>
          <Link to="/jf/admin" style={{ color: '#ff4500', fontSize: 12, fontWeight: 'bold' }}>Admin Panel</Link>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Admin Page
// ---------------------------------------------------------------------------

function AdminPage({ token, categories }: { token: string; categories: Category[] }) {
  const [tab, setTab] = useState<'errors' | 'users' | 'comments' | 'categories' | 'feeds' | 'stories'>('errors');
  const [users, setUsers] = useState<any[]>([]);
  const [comments, setComments] = useState<any[]>([]);
  const [stories, setStories] = useState<any[]>([]);
  const [errors, setErrors] = useState<any[]>([]);
  const [errorCounts, setErrorCounts] = useState<Record<string, number>>({});
  const [errorFilter, setErrorFilter] = useState<string>('');
  const [errorTotal, setErrorTotal] = useState(0);
  const [editingStory, setEditingStory] = useState<number | null>(null);
  const [storyEdits, setStoryEdits] = useState<any>({});
  const [msgUserId, setMsgUserId] = useState<number | null>(null);
  const [msgContent, setMsgContent] = useState('');
  const [newCatName, setNewCatName] = useState('');
  const [newCatParent, setNewCatParent] = useState('');
  const [newFeedName, setNewFeedName] = useState('');
  const [newFeedUrl, setNewFeedUrl] = useState('');
  const [newFeedCat, setNewFeedCat] = useState('');
  const [newFeedIF, setNewFeedIF] = useState('');
  const [status, setStatus] = useState('');

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const loadUsers = () => fetch('/api/admin/users', { headers }).then(r => r.json()).then(d => setUsers(d.users || []));
  const loadComments = () => fetch('/api/admin/comments', { headers }).then(r => r.json()).then(d => setComments(d.comments || []));
  const loadStories = () => fetch('/api/admin/stories', { headers }).then(r => r.json()).then(d => setStories(d.stories || []));
  const loadErrors = (cat?: string) => {
    const catParam = cat ? `&category=${cat}` : '';
    fetch(`/api/admin/errors?resolved=false&per_page=100${catParam}`, { headers })
      .then(r => r.json())
      .then(d => {
        setErrors(d.errors || []);
        setErrorCounts(d.category_counts || {});
        setErrorTotal(d.total || 0);
      });
  };

  useEffect(() => { loadUsers(); loadComments(); loadStories(); loadErrors(); }, []);  // eslint-disable-line

  const toggleDisable = async (uid: number) => {
    await fetch(`/api/admin/users/${uid}/disable`, { method: 'POST', headers });
    loadUsers();
  };

  const toggleAdmin = async (uid: number) => {
    await fetch(`/api/admin/users/${uid}/toggle-admin`, { method: 'POST', headers });
    loadUsers();
  };

  const sendMessage = async (uid: number) => {
    if (!msgContent.trim()) return;
    await fetch(`/api/admin/users/${uid}/message`, { method: 'POST', headers, body: JSON.stringify({ content: msgContent }) });
    setMsgContent('');
    setMsgUserId(null);
    setStatus('Message sent');
    setTimeout(() => setStatus(''), 3000);
  };

  const deleteComment = async (cid: number) => {
    await fetch(`/api/admin/comments/${cid}`, { method: 'DELETE', headers });
    loadComments();
  };

  const addCategory = async () => {
    if (!newCatName.trim()) return;
    const res = await fetch('/api/admin/categories', {
      method: 'POST', headers,
      body: JSON.stringify({ name: newCatName, parent_id: newCatParent ? parseInt(newCatParent) : null }),
    });
    const data = await res.json();
    setStatus(res.ok ? `Category "${data.name}" created` : data.message);
    setNewCatName('');
    setNewCatParent('');
    setTimeout(() => setStatus(''), 3000);
  };

  const addFeed = async () => {
    if (!newFeedName.trim() || !newFeedUrl.trim() || !newFeedCat) return;
    const res = await fetch('/api/admin/feeds', {
      method: 'POST', headers,
      body: JSON.stringify({ name: newFeedName, url: newFeedUrl, category_id: parseInt(newFeedCat), impact_factor: newFeedIF || null }),
    });
    const data = await res.json();
    setStatus(res.ok ? `Feed "${data.name}" created` : data.message);
    setNewFeedName('');
    setNewFeedUrl('');
    setNewFeedIF('');
    setTimeout(() => setStatus(''), 3000);
  };

  const flatCats = (cats: Category[], depth = 0): { id: number; name: string; depth: number }[] => {
    const r: { id: number; name: string; depth: number }[] = [];
    for (const c of cats) {
      r.push({ id: c.id, name: c.name, depth });
      if (c.subcategories) r.push(...flatCats(c.subcategories, depth + 1));
    }
    return r;
  };

  const startEditStory = (s: any) => {
    setEditingStory(s.id);
    setStoryEdits({ headline: s.headline, summary: s.summary, source_name: s.source_name, source_url: s.source_url, paper_title: s.paper_title || '', paper_doi: s.paper_doi || '', topic: s.topic });
  };

  const saveStory = async (sid: number) => {
    await fetch(`/api/admin/stories/${sid}`, { method: 'PUT', headers, body: JSON.stringify(storyEdits) });
    setEditingStory(null);
    setStoryEdits({});
    setStatus('Story updated');
    loadStories();
    setTimeout(() => setStatus(''), 3000);
  };

  const deleteStory = async (sid: number) => {
    if (!window.confirm('Delete this story permanently?')) return;
    await fetch(`/api/admin/stories/${sid}`, { method: 'DELETE', headers });
    setStatus('Story deleted');
    loadStories();
    setTimeout(() => setStatus(''), 3000);
  };

  const unresolvedCount = Object.values(errorCounts).reduce((a, b) => a + b, 0);
  const adminTabs = [
    { key: 'errors' as const, label: `Errors${unresolvedCount > 0 ? ` (${unresolvedCount})` : ''}` },
    { key: 'stories' as const, label: `Stories (${stories.length})` },
    { key: 'users' as const, label: `Users (${users.length})` },
    { key: 'comments' as const, label: `Comments (${comments.length})` },
    { key: 'categories' as const, label: 'Add Category' },
    { key: 'feeds' as const, label: 'Add Source' },
  ];

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: 20 }}>
      <h2 style={{ fontSize: 16, marginBottom: 16, color: '#ff4500' }}>Admin Panel</h2>
      {status && <div style={{ background: '#e8f5e9', padding: '6px 12px', fontSize: 12, marginBottom: 12, borderRadius: 4 }}>{status}</div>}

      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #eee', marginBottom: 16 }}>
        {adminTabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className="nav-tab" style={{ fontWeight: tab === t.key ? 'bold' : 'normal', borderBottom: tab === t.key ? '2px solid #ff4500' : 'none' }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'errors' && (
        <div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <button onClick={() => { setErrorFilter(''); loadErrors(); }}
              className="action-btn" style={{ fontSize: 10, fontWeight: !errorFilter ? 'bold' : 'normal', background: !errorFilter ? '#5f99cf' : undefined, color: !errorFilter ? '#fff' : undefined }}>
              All ({unresolvedCount})
            </button>
            {Object.entries(errorCounts).map(([cat, cnt]) => (
              <button key={cat} onClick={() => { setErrorFilter(cat); loadErrors(cat); }}
                className="action-btn" style={{ fontSize: 10, fontWeight: errorFilter === cat ? 'bold' : 'normal', background: errorFilter === cat ? '#5f99cf' : undefined, color: errorFilter === cat ? '#fff' : undefined }}>
                {cat} ({cnt})
              </button>
            ))}
            {unresolvedCount > 0 && (
              <button onClick={async () => {
                await fetch('/api/admin/errors/resolve-all', { method: 'POST', headers, body: JSON.stringify({ category: errorFilter || undefined }) });
                loadErrors(errorFilter || undefined);
                setStatus('Errors resolved');
                setTimeout(() => setStatus(''), 3000);
              }} className="link-btn" style={{ fontSize: 10, color: '#ff4500', marginLeft: 'auto' }}>
                resolve all{errorFilter ? ` ${errorFilter}` : ''}
              </button>
            )}
          </div>
          {errors.length === 0 && <p style={{ fontSize: 12, color: '#888' }}>No unresolved errors.</p>}
          {errors.map(e => (
            <div key={e.id} style={{ marginBottom: 10, fontSize: 12, borderBottom: '1px solid #eee', paddingBottom: 8, borderLeft: `3px solid ${e.severity === 'error' ? '#ff4500' : '#DAA520'}`, paddingLeft: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <span style={{ fontWeight: 'bold', color: e.severity === 'error' ? '#ff4500' : '#DAA520', marginRight: 6, fontSize: 10, textTransform: 'uppercase' }}>{e.severity}</span>
                  <span style={{ color: '#888', fontSize: 10, marginRight: 6 }}>[{e.category}]</span>
                  <span>{e.message}</span>
                </div>
                <button onClick={async () => {
                  await fetch(`/api/admin/errors/${e.id}/resolve`, { method: 'POST', headers });
                  loadErrors(errorFilter || undefined);
                }} className="link-btn" style={{ fontSize: 10, color: '#228B22', flexShrink: 0 }}>resolve</button>
              </div>
              {e.source && <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>Source: <code style={{ background: '#f5f5f5', padding: '1px 4px' }}>{e.source}</code></div>}
              {e.details && <details style={{ fontSize: 10, color: '#888', marginTop: 4 }}><summary>Details</summary><pre style={{ whiteSpace: 'pre-wrap', fontSize: 10, maxHeight: 200, overflow: 'auto', background: '#f8f8f8', padding: 6 }}>{e.details}</pre></details>}
              <div style={{ fontSize: 9, color: '#aaa', marginTop: 2 }}>{e.created_at ? new Date(e.created_at).toLocaleString() : ''}</div>
            </div>
          ))}
        </div>
      )}

      {tab === 'users' && (
        <div>
          <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '2px solid #ccc' }}>
                <th style={{ padding: 4 }}>User</th><th>Email</th><th>Joined</th><th>Status</th><th>Admin</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} style={{ borderBottom: '1px solid #eee', opacity: u.disabled ? 0.5 : 1 }}>
                  <td style={{ padding: 4 }}><b>{u.username}</b></td>
                  <td>{u.email}</td>
                  <td>{u.created_at ? new Date(u.created_at).toLocaleDateString() : ''}</td>
                  <td>{u.disabled ? 'Disabled' : u.email_verified ? 'Active' : 'Unverified'}</td>
                  <td style={{ textAlign: 'center' }}>
                    <input type="checkbox" checked={u.is_admin} onChange={() => toggleAdmin(u.id)}
                      title={u.is_admin ? 'Remove admin' : 'Make admin'} />
                  </td>
                  <td>
                    <button onClick={() => toggleDisable(u.id)} className="link-btn" style={{ color: u.disabled ? '#228B22' : '#ff4500' }}>
                      {u.disabled ? 'Enable' : 'Disable'}
                    </button>
                    {' '}
                    <button onClick={() => setMsgUserId(msgUserId === u.id ? null : u.id)} className="link-btn">msg</button>
                    {msgUserId === u.id && (
                      <div style={{ marginTop: 4 }}>
                        <input value={msgContent} onChange={e => setMsgContent(e.target.value)} placeholder="Message..."
                          style={{ fontSize: 11, padding: 2, width: 200, border: '1px solid #ccc' }} />
                        <button onClick={() => sendMessage(u.id)} className="link-btn" style={{ marginLeft: 4 }}>send</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'comments' && (
        <div>
          {comments.length === 0 && <p style={{ fontSize: 12, color: '#888' }}>No comments yet.</p>}
          {comments.map(c => (
            <div key={c.id} style={{ marginBottom: 10, fontSize: 12, borderBottom: '1px solid #eee', paddingBottom: 8 }}>
              <div style={{ color: '#888', marginBottom: 4 }}>
                <b>{c.username}</b> on{' '}
                {c.article_slug ? <Link to={`/jf/article/${c.article_slug}`} style={{ color: '#5f99cf' }}>{c.article_title}</Link> : 'deleted article'}
                <span style={{ marginLeft: 8 }}>{c.created_at ? new Date(c.created_at).toLocaleDateString() : ''}</span>
                <button onClick={() => deleteComment(c.id)} className="link-btn" style={{ color: '#ff4500', marginLeft: 8 }}>delete</button>
              </div>
              <div>{c.content}</div>
            </div>
          ))}
        </div>
      )}

      {tab === 'categories' && (
        <div style={{ maxWidth: 400 }}>
          <p style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>Add a new category, sub-category, or sub-sub-category:</p>
          <div style={{ marginBottom: 8 }}>
            <label style={{ fontSize: 11, fontWeight: 'bold', display: 'block', marginBottom: 2 }}>Category Name *</label>
            <input value={newCatName} onChange={e => setNewCatName(e.target.value)}
              style={{ width: '100%', padding: '4px 6px', border: '1px solid #ccc', fontSize: 12, boxSizing: 'border-box' }} />
          </div>
          <div style={{ marginBottom: 8 }}>
            <label style={{ fontSize: 11, fontWeight: 'bold', display: 'block', marginBottom: 2 }}>Parent Category (leave empty for top-level)</label>
            <select value={newCatParent} onChange={e => setNewCatParent(e.target.value)}
              style={{ width: '100%', padding: '4px 6px', border: '1px solid #ccc', fontSize: 12 }}>
              <option value="">None (top-level)</option>
              {flatCats(categories).map(c => (
                <option key={c.id} value={c.id}>{'  '.repeat(c.depth) + c.name}</option>
              ))}
            </select>
          </div>
          <button onClick={addCategory} className="create-post-btn" style={{ fontSize: 12, padding: '4px 14px' }}>Add Category</button>
        </div>
      )}

      {tab === 'feeds' && (
        <div style={{ maxWidth: 400 }}>
          <p style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>Add a new RSS feed source:</p>
          <div style={{ marginBottom: 8 }}>
            <label style={{ fontSize: 11, fontWeight: 'bold', display: 'block', marginBottom: 2 }}>Journal Name *</label>
            <input value={newFeedName} onChange={e => setNewFeedName(e.target.value)}
              style={{ width: '100%', padding: '4px 6px', border: '1px solid #ccc', fontSize: 12, boxSizing: 'border-box' }} />
          </div>
          <div style={{ marginBottom: 8 }}>
            <label style={{ fontSize: 11, fontWeight: 'bold', display: 'block', marginBottom: 2 }}>RSS Feed URL *</label>
            <input value={newFeedUrl} onChange={e => setNewFeedUrl(e.target.value)}
              style={{ width: '100%', padding: '4px 6px', border: '1px solid #ccc', fontSize: 12, boxSizing: 'border-box' }} />
          </div>
          <div style={{ marginBottom: 8 }}>
            <label style={{ fontSize: 11, fontWeight: 'bold', display: 'block', marginBottom: 2 }}>Category *</label>
            <select value={newFeedCat} onChange={e => setNewFeedCat(e.target.value)}
              style={{ width: '100%', padding: '4px 6px', border: '1px solid #ccc', fontSize: 12 }}>
              <option value="">Select category</option>
              {flatCats(categories).map(c => (
                <option key={c.id} value={c.id}>{'  '.repeat(c.depth) + c.name}</option>
              ))}
            </select>
          </div>
          <div style={{ marginBottom: 8 }}>
            <label style={{ fontSize: 11, fontWeight: 'bold', display: 'block', marginBottom: 2 }}>Impact Factor</label>
            <input value={newFeedIF} onChange={e => setNewFeedIF(e.target.value)} type="number" step="0.1"
              style={{ width: '100%', padding: '4px 6px', border: '1px solid #ccc', fontSize: 12, boxSizing: 'border-box' }} />
          </div>
          <button onClick={addFeed} className="create-post-btn" style={{ fontSize: 12, padding: '4px 14px' }}>Add Source</button>
        </div>
      )}

      {tab === 'stories' && (
        <div>
          {stories.length === 0 && <p style={{ fontSize: 12, color: '#888' }}>No stories yet.</p>}
          {stories.map(s => (
            <div key={s.id} style={{ marginBottom: 16, borderBottom: '1px solid #eee', paddingBottom: 12 }}>
              {editingStory === s.id ? (
                <div style={{ fontSize: 12 }}>
                  <div style={{ marginBottom: 6 }}>
                    <label style={{ fontSize: 11, fontWeight: 'bold', display: 'block', marginBottom: 2 }}>Headline</label>
                    <input value={storyEdits.headline} onChange={e => setStoryEdits({ ...storyEdits, headline: e.target.value })}
                      style={{ width: '100%', padding: '4px 6px', border: '1px solid #ccc', fontSize: 12, boxSizing: 'border-box' }} />
                  </div>
                  <div style={{ marginBottom: 6 }}>
                    <label style={{ fontSize: 11, fontWeight: 'bold', display: 'block', marginBottom: 2 }}>Summary</label>
                    <textarea value={storyEdits.summary} onChange={e => setStoryEdits({ ...storyEdits, summary: e.target.value })}
                      rows={6} style={{ width: '100%', padding: '4px 6px', border: '1px solid #ccc', fontSize: 12, boxSizing: 'border-box', lineHeight: 1.5 }} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 6 }}>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 'bold', display: 'block', marginBottom: 2 }}>Source Name</label>
                      <input value={storyEdits.source_name} onChange={e => setStoryEdits({ ...storyEdits, source_name: e.target.value })}
                        style={{ width: '100%', padding: '4px 6px', border: '1px solid #ccc', fontSize: 12, boxSizing: 'border-box' }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 'bold', display: 'block', marginBottom: 2 }}>Source URL</label>
                      <input value={storyEdits.source_url} onChange={e => setStoryEdits({ ...storyEdits, source_url: e.target.value })}
                        style={{ width: '100%', padding: '4px 6px', border: '1px solid #ccc', fontSize: 12, boxSizing: 'border-box' }} />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 6 }}>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 'bold', display: 'block', marginBottom: 2 }}>Paper Title</label>
                      <input value={storyEdits.paper_title} onChange={e => setStoryEdits({ ...storyEdits, paper_title: e.target.value })}
                        style={{ width: '100%', padding: '4px 6px', border: '1px solid #ccc', fontSize: 12, boxSizing: 'border-box' }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 'bold', display: 'block', marginBottom: 2 }}>Paper DOI</label>
                      <input value={storyEdits.paper_doi} onChange={e => setStoryEdits({ ...storyEdits, paper_doi: e.target.value })}
                        style={{ width: '100%', padding: '4px 6px', border: '1px solid #ccc', fontSize: 12, boxSizing: 'border-box' }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 'bold', display: 'block', marginBottom: 2 }}>Topic</label>
                      <select value={storyEdits.topic} onChange={e => setStoryEdits({ ...storyEdits, topic: e.target.value })}
                        style={{ width: '100%', padding: '4px 6px', border: '1px solid #ccc', fontSize: 12 }}>
                        <option value="health">health</option>
                        <option value="science">science</option>
                        <option value="psychology">psychology</option>
                        <option value="law">law</option>
                        <option value="education">education</option>
                      </select>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <button onClick={() => saveStory(s.id)} className="create-post-btn" style={{ fontSize: 11, padding: '3px 12px' }}>Save</button>
                    <button onClick={() => setEditingStory(null)} className="link-btn" style={{ fontSize: 11 }}>Cancel</button>
                  </div>
                </div>
              ) : (
                <div style={{ fontSize: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <span className={`story-topic-badge topic-${s.topic}`} style={{ marginRight: 6 }}>{s.topic}</span>
                      <b>{s.headline}</b>
                      <div style={{ color: '#888', fontSize: 11, marginTop: 2 }}>
                        {s.source_name} &middot; {s.created_at ? new Date(s.created_at).toLocaleString() : ''}
                      </div>
                      <div style={{ color: '#666', marginTop: 4, lineHeight: 1.5, maxHeight: 60, overflow: 'hidden' }}>{s.summary}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginLeft: 12, flexShrink: 0 }}>
                      <button onClick={() => startEditStory(s)} className="link-btn" style={{ color: '#5f99cf' }}>edit</button>
                      <button onClick={() => deleteStory(s.id)} className="link-btn" style={{ color: '#ff4500' }}>delete</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [status, setStatus] = useState<'form' | 'loading' | 'success' | 'error'>('form');
  const [message, setMessage] = useState('');
  const token = searchParams.get('token') || '';

  if (!token && status === 'form') return (
    <div style={{ padding: 40, textAlign: 'center' }}>
      <p style={{ color: '#ff4500', fontSize: 13 }}>Missing reset token.</p>
    </div>
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirm) { setMessage('Passwords do not match.'); setStatus('error'); return; }
    if (password.length < 6) { setMessage('Password must be at least 6 characters.'); setStatus('error'); return; }
    setStatus('loading');
    try {
      const res = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();
      if (res.ok) {
        setStatus('success');
        setMessage('Password reset! Redirecting to login...');
        setTimeout(() => navigate('/'), 2000);
      } else {
        setStatus('error');
        setMessage(data.message || 'Reset failed.');
      }
    } catch { setStatus('error'); setMessage('Network error.'); }
  };

  return (
    <div style={{ padding: 40, maxWidth: 350, margin: '0 auto' }}>
      <h2 style={{ fontSize: 16, marginBottom: 16 }}>reset password</h2>
      {(status === 'error' || status === 'success') && (
        <p style={{ fontSize: 12, color: status === 'error' ? '#ff4500' : '#228B22', marginBottom: 10 }}>{message}</p>
      )}
      {(status === 'form' || status === 'error') && (
        <form className="auth-form" onSubmit={handleSubmit}>
          <label>new password</label>
          <input type="password" value={password} onChange={e => setPassword(e.target.value)} required autoFocus />
          <label>confirm password</label>
          <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} required />
          <button type="submit">reset password</button>
        </form>
      )}
      {status === 'loading' && <p style={{ fontSize: 12, color: '#888' }}>Resetting...</p>}
      {status === 'success' && <p style={{ fontSize: 12, color: '#888' }}>{message}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// App Root
// ---------------------------------------------------------------------------

function AppInner() {
  const { lang, t, apiLang } = useI18n();
  const [categories, setCategories] = useState<Category[]>([]);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [user, setUser] = useState<any>(null);
  const [showLogin, setShowLogin] = useState(false);
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');
  const [messages, setMessages] = useState<any[]>([]);
  const [showMessages, setShowMessages] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Helper to localize API URLs
  const api = useCallback((url: string) => localizeApiUrl(url, apiLang), [apiLang]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.setAttribute('data-lang', lang);
    localStorage.setItem('theme', theme);
  }, [theme, lang]);

  useEffect(() => {
    fetch(api('/api/categories')).then(res => res.json()).then(setCategories);
    if (token) {
      fetch('/api/profile', { headers: { Authorization: `Bearer ${token}` } })
        .then(res => {
          if (!res.ok) { setToken(null); setUser(null); localStorage.removeItem('token'); return null; }
          return res.json();
        })
        .then(data => {
          if (!data) return;
          setUser(data);
          if (!data.onboarding_complete) setShowOnboarding(true);
        })
        .catch(() => { setToken(null); setUser(null); localStorage.removeItem('token'); });
    }
  }, [token]);

  const handleLogin = (newToken: string, userData: { username: string; onboarding_complete: boolean }) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    if (!userData.onboarding_complete) {
      setShowOnboarding(true);
    }
  };

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    setUser((prev: any) => prev ? { ...prev, onboarding_complete: true } : prev);
  };

  const fetchMessages = async () => {
    if (!token) return setShowLogin(true);
    const res = await fetch('/api/messages', { headers: { Authorization: `Bearer ${token}` } });
    setMessages(await res.json());
    setShowMessages(true);
  };

  const commonProps = {
    categories, token, user, setShowLogin, setUser, theme, fetchMessages, api, lang, t,
    toggleTheme: () => setTheme(theme === 'light' ? 'dark' : 'light'),
  };

  return (
    <BrowserRouter basename={process.env.PUBLIC_URL || ""}>
      <ScrollToTop />
      <AuthModal show={showLogin} onClose={() => setShowLogin(false)} onLogin={handleLogin} />

      {showOnboarding && token && (
        <Onboarding token={token} categories={categories} onComplete={handleOnboardingComplete} />
      )}
      {showMessages && (
        <div className="modal-overlay" onClick={() => setShowMessages(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}
            style={{ position: 'relative', width: '80%', maxWidth: 600, maxHeight: '80vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #5f99cf', marginBottom: '10px' }}>
              <h2 style={{ margin: 0, fontSize: 16 }}>Messages</h2>
              <button onClick={() => setShowMessages(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}>Close</button>
            </div>
            {messages.map(m => (
              <div key={m.id} style={{ padding: '5px 0', borderBottom: '1px solid #eee' }}>
                <b>{m.sender} &rarr; {m.receiver}</b>: {m.content}
              </div>
            ))}
          </div>
        </div>
      )}

      <Routes>
        <Route path="/" element={<Layout {...commonProps}><HomePage token={token} setShowLogin={setShowLogin} /></Layout>} />
        <Route path="/med" element={<Layout {...commonProps}><HomePage token={token} setShowLogin={setShowLogin} defaultTopic="health" /></Layout>} />
        <Route path="/sci" element={<Layout {...commonProps}><HomePage token={token} setShowLogin={setShowLogin} defaultTopic="science" /></Layout>} />
        <Route path="/psy" element={<Layout {...commonProps}><HomePage token={token} setShowLogin={setShowLogin} defaultTopic="psychology" /></Layout>} />
        <Route path="/law" element={<Layout {...commonProps}><HomePage token={token} setShowLogin={setShowLogin} defaultTopic="law" /></Layout>} />
        <Route path="/edu" element={<Layout {...commonProps}><HomePage token={token} setShowLogin={setShowLogin} defaultTopic="education" /></Layout>} />
        <Route path="/hotp" element={<Layout {...commonProps}><HomePage token={token} setShowLogin={setShowLogin} defaultTopic="hotp" /></Layout>} />
        <Route path="/story/:slug" element={<Layout {...commonProps}><StoryPage token={token} setShowLogin={setShowLogin} /></Layout>} />
        <Route path="/about" element={<Layout {...commonProps}><AboutPage setShowLogin={setShowLogin} /></Layout>} />
        <Route path="/verify" element={<VerifyEmail onLogin={handleLogin} />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/jf" element={<FeedPage {...commonProps} />} />
        <Route path="/jf/profile" element={
          <Layout {...commonProps}><ProfilePage token={token} categories={categories} /></Layout>
        } />
        <Route path="/jf/admin" element={
          token ? <Layout {...commonProps}><AdminPage token={token} categories={categories} /></Layout> : <FeedPage {...commonProps} />
        } />
        <Route path="/jf/lists" element={
          token ? <Layout {...commonProps}><BookmarkListsPage token={token} /></Layout> : <FeedPage {...commonProps} />
        } />
        <Route path="/jf/list/:slug" element={
          <Layout {...commonProps}><PublicListView token={token} /></Layout>
        } />
        <Route path="/jf/submit" element={
          <Layout {...commonProps}><SubmitPostPage token={token} categories={categories} /></Layout>
        } />
        <Route path="/jf/trending" element={<TrendingPage {...commonProps} />} />
        {/* /jf/hotp redirects to /hotp */}
        <Route path="/jf/article/:slug/jfif" element={token ? <Layout {...commonProps}><JFIFBreakdownPage token={token} /></Layout> : <Layout {...commonProps}><AboutPage /></Layout>} />
        <Route path="/jf/article/:slug" element={<ArticlePage {...commonProps} />} />
        <Route path="/jf/:cat1" element={<FeedPage {...commonProps} />} />
        <Route path="/jf/:cat1/:cat2" element={<FeedPage {...commonProps} />} />
        <Route path="/jf/:cat1/:cat2/:cat3" element={<FeedPage {...commonProps} />} />
        <Route path="/jf/:cat1/:cat2/:cat3/:cat4" element={<FeedPage {...commonProps} />} />
        <Route path="/jf/*" element={<Layout {...commonProps}><NotFoundPage /></Layout>} />
      </Routes>
    </BrowserRouter>
  );
}

function App() {
  return (
    <I18nProvider>
      <AppInner />
    </I18nProvider>
  );
}

export default App;
